"use client"

import React from "react"
import { useEffect, useState } from "react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Label } from "@/components/ui/label"
import { Loader2 } from "lucide-react"
import { useUsers } from "@/lib/contexts/user-context"
import { usePage } from "@inertiajs/react"

// Helper function to organize departments hierarchically for select dropdown
const organizeDepartmentsForSelect = (departments) => {
  const departmentMap = new Map()
  const result = []

  // First pass: create map of all departments
  departments.forEach(dept => {
    departmentMap.set(dept.id, { ...dept, depth: 0 })
  })

  // Second pass: build hierarchy and calculate depth
  const buildHierarchy = (dept, depth = 0) => {
    dept.depth = depth
    result.push(dept)
    
    // Add children
    departments.forEach(child => {
      if (child.parent_id === dept.id) {
        buildHierarchy(departmentMap.get(child.id), depth + 1)
      }
    })
  }

  // Start with root departments (no parent)
  departments.forEach(dept => {
    if (!dept.parent_id) {
      buildHierarchy(departmentMap.get(dept.id), 0)
    }
  })

  return result
}

export function UserFormModal({ open, onOpenChange, initialData, onSubmit, isSubmitting = false, departments = [] }) {
  const { currentUser } = useUsers()
  const isEditing = !!initialData?.id
  const [errors, setErrors] = useState({})
  
  const [formData, setFormData] = useState({
    firstName: initialData?.firstName || "",
    lastName: initialData?.lastName || "",
    email: initialData?.email || "",
    role: initialData?.role || "user",
    status: initialData?.status || "Active",
    department_id: initialData?.department_id ? initialData.department_id.toString() : "0",
  })

  // Check if current user is admin
  const isAdmin = currentUser?.role === "admin" || currentUser?.role === "Admin"
  const isViewOnly = false // For now, allow all editing since we're checking permissions on the backend

  useEffect(() => {
    if (open) {
      if (initialData) {
        // Editing existing user
        setFormData({
          ...initialData,
          status: initialData?.status || "Active",
          department_id: initialData?.department_id ? initialData.department_id.toString() : "0",
        })
      } else {
        // Adding new user
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          role: "user",
          status: "Active",
          department_id: "0",
        })
      }
      setErrors({})
    } else {
      // Reset form when modal closes - small delay to avoid flickering during close animation
      const timeout = setTimeout(() => {
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          role: "user",
          status: "Active",
          department_id: "0",
        })
        setErrors({})
      }, 300)
      return () => clearTimeout(timeout)
    }
  }, [open, initialData])

  const validateForm = () => {
    const newErrors = {}

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required"
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required"
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required"
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid"
    }

    if (!formData.role) {
      newErrors.role = "Role is required"
    }

    if (!formData.status) {
      newErrors.status = "Status is required"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (validateForm()) {
      try {
        // Prepare form data for submission, converting "0" back to null for department_id
        const submitData = {
          ...formData,
          department_id: formData.department_id === "0" ? null : formData.department_id
        }
        
        // Call the submit function
        await onSubmit(submitData)
        
        // Wait a moment before closing the modal
        setTimeout(() => {
          // Close the modal
          onOpenChange(false)
        }, 800)
      } catch (error) {
        // Let the parent component handle the error
        throw error
      }
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-10/12 sm:w-full">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit User" : "Add New User"}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? "Make changes to the user's information here."
              : "Fill in the details to create a new user. A welcome email will be sent to set up their password."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  disabled={isViewOnly}
                  className={errors.firstName ? "border-red-500" : ""}
                />
                {errors.firstName && <p className="text-sm text-red-500">{errors.firstName}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  disabled={isViewOnly}
                  className={errors.lastName ? "border-red-500" : ""}
                />
                {errors.lastName && <p className="text-sm text-red-500">{errors.lastName}</p>}
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                disabled={isViewOnly}
                className={errors.email ? "border-red-500" : ""}
              />
              {errors.email && <p className="text-sm text-red-500">{errors.email}</p>}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                  disabled={isViewOnly}
                >
                  <SelectTrigger id="role" className={errors.role ? "border-red-500" : ""}>
                    <SelectValue placeholder="Select a role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="user">User</SelectItem>
                  </SelectContent>
                </Select>
                {errors.role && <p className="text-sm text-red-500">{errors.role}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                  disabled={isViewOnly}
                >
                  <SelectTrigger id="status" className={errors.status ? "border-red-500" : ""}>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
                {errors.status && <p className="text-sm text-red-500">{errors.status}</p>}
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="department">Department</Label>
              <Select
                value={formData.department_id}
                onValueChange={(value) => setFormData({ ...formData, department_id: value })}
                disabled={isViewOnly}
              >
                <SelectTrigger id="department" className={errors.department_id ? "border-red-500" : ""}>
                  <SelectValue placeholder="Select a department (optional)">
                    {formData.department_id && formData.department_id !== "0" && (() => {
                      const selectedDept = departments.find(d => d.id.toString() === formData.department_id)
                      return selectedDept ? (
                        <div className="flex items-center">
                          <div 
                            className={`w-4 h-4 rounded-full flex-shrink-0 mr-2 ${
                              selectedDept.settings?.icon_color === 'blue' ? 'bg-gradient-to-r from-blue-400 to-blue-600' :
                              selectedDept.settings?.icon_color === 'green' ? 'bg-gradient-to-r from-green-400 to-green-600' :
                              selectedDept.settings?.icon_color === 'red' ? 'bg-gradient-to-r from-red-400 to-red-600' :
                              selectedDept.settings?.icon_color === 'yellow' ? 'bg-gradient-to-r from-yellow-400 to-yellow-600' :
                              selectedDept.settings?.icon_color === 'purple' ? 'bg-gradient-to-r from-purple-400 to-purple-600' :
                              selectedDept.settings?.icon_color === 'pink' ? 'bg-gradient-to-r from-pink-400 to-pink-600' :
                              selectedDept.settings?.icon_color === 'indigo' ? 'bg-gradient-to-r from-indigo-400 to-indigo-600' :
                              selectedDept.settings?.icon_color === 'orange' ? 'bg-gradient-to-r from-orange-400 to-orange-600' :
                              selectedDept.settings?.icon_color === 'teal' ? 'bg-gradient-to-r from-teal-400 to-teal-600' :
                              selectedDept.settings?.icon_color === 'cyan' ? 'bg-gradient-to-r from-cyan-400 to-cyan-600' :
                              'bg-gradient-to-r from-blue-400 to-blue-600'
                            }`}
                          ></div>
                          <span>{selectedDept.name}</span>
                        </div>
                      ) : null
                    })()}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">No Department</SelectItem>
                  {organizeDepartmentsForSelect(departments).map((dept) => (
                    <SelectItem key={dept.id} value={dept.id.toString()}>
                      <div className="flex items-center w-full">
                        <div 
                          className={`w-4 h-4 rounded-full flex-shrink-0 mr-2 ${
                            dept.settings?.icon_color === 'blue' ? 'bg-gradient-to-r from-blue-400 to-blue-600' :
                            dept.settings?.icon_color === 'green' ? 'bg-gradient-to-r from-green-400 to-green-600' :
                            dept.settings?.icon_color === 'red' ? 'bg-gradient-to-r from-red-400 to-red-600' :
                            dept.settings?.icon_color === 'yellow' ? 'bg-gradient-to-r from-yellow-400 to-yellow-600' :
                            dept.settings?.icon_color === 'purple' ? 'bg-gradient-to-r from-purple-400 to-purple-600' :
                            dept.settings?.icon_color === 'pink' ? 'bg-gradient-to-r from-pink-400 to-pink-600' :
                            dept.settings?.icon_color === 'indigo' ? 'bg-gradient-to-r from-indigo-400 to-indigo-600' :
                            dept.settings?.icon_color === 'orange' ? 'bg-gradient-to-r from-orange-400 to-orange-600' :
                            dept.settings?.icon_color === 'teal' ? 'bg-gradient-to-r from-teal-400 to-teal-600' :
                            dept.settings?.icon_color === 'cyan' ? 'bg-gradient-to-r from-cyan-400 to-cyan-600' :
                            'bg-gradient-to-r from-blue-400 to-blue-600'
                          }`}
                          style={{ marginLeft: `${dept.depth * 16}px` }}
                        ></div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">
                            {dept.name}
                          </div>
                          {dept.parent && (
                            <div className="text-xs text-muted-foreground truncate">
                              {dept.parent.name}
                            </div>
                          )}
                        </div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.department_id && <p className="text-sm text-red-500">{errors.department_id}</p>}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {isViewOnly ? "Close" : "Cancel"}
            </Button>
            {!isViewOnly && (
              <Button type="submit" disabled={isSubmitting} className="dark:text-white">
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isEditing ? "Update User" : "Create User"}
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
